import {
  CheckCircleOutlinedIcon,
  HighlightOffOutlinedIcon,
  VisibilityIcon,
  VisibilityOffIcon,
} from '@assets/icons';
import {
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TextField.scss';
import { Button } from '..';
import { COLORS } from '@utils/constants/color';

const AppTextField: FC<AppTextFieldProps> = ({
  id,
  label,
  required,
  disabled,
  placeholder,
  value,
  defaultValue,
  type,
  isRow = false,
  autoFocus,
  maxLength,
  onChange,
  error,
  icon,
  width = '100%',
  isShowText = true,
  isPreRegistration = false,
  isSms = false,
  isVerifyPhone,
  onClickSms,
}) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const isTypePassword = useMemo(() => type === 'password', [type]);

  const inputType = useMemo(() => {
    if (!isTypePassword) return type;
    return showPassword ? 'text' : 'password';
  }, [type, showPassword]);

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('common.require_input_text');
    }

    return error?.message ? t(error?.message) : null;
  }, [error]);

  const handleShowPassword = () => setShowPassword(show => !show);

  const handleMouseIcon = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Grid
      container
      className='TextField'
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid
          item
          xs={4}
          alignItems='center'
          justifyContent={isRow ? 'flex-end' : 'flex-start'}
          textAlign={isRow ? 'right' : undefined}
          className={isPreRegistration ? 'pre-label' : ''}
        >
          <FormLabel
            htmlFor={id}
            required={isPreRegistration ? undefined : required}
          >
            {t(label)}
            {isPreRegistration && (
              <Typography className='required'>
                {t('common.required')}
              </Typography>
            )}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8}>
        <TextField
          id={id}
          name={id}
          disabled={disabled}
          type={inputType}
          placeholder={t(placeholder!)}
          value={value}
          defaultValue={defaultValue}
          sx={{
            width: width,
            '& .MuiInputBase-input': {
              paddingLeft: isSms ? '0 !important' : '14px',
            },
          }}
          autoFocus={autoFocus}
          InputProps={{
            startAdornment: isSms ? (
              <InputAdornment position='start'>
                {isVerifyPhone ? (
                  <CheckCircleOutlinedIcon
                    style={{ fontSize: '32px', color: COLORS.success.main }}
                  />
                ) : (
                  <HighlightOffOutlinedIcon
                    style={{ fontSize: '32px', color: COLORS.error.main }}
                  />
                )}
              </InputAdornment>
            ) : undefined,
            endAdornment: !isTypePassword ? (
              icon ? (
                <InputAdornment position='end'>
                  <IconButton tabIndex={-1} aria-label='icon-input' edge='end'>
                    {icon}
                  </IconButton>
                </InputAdornment>
              ) : isSms ? (
                <Button
                  title='common.resend_message'
                  sx={{ whiteSpace: 'nowrap', height: 35, width: 90 }}
                  onClick={() => {
                    if (onClickSms) {
                      onClickSms();
                    }
                  }}
                />
              ) : undefined
            ) : (
              <InputAdornment position='end'>
                <IconButton
                  tabIndex={-1}
                  aria-label='toggle password visibility'
                  onClick={handleShowPassword}
                  onMouseDown={handleMouseIcon}
                  onMouseUp={handleMouseIcon}
                  edge='end'
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{ maxLength: isTypePassword ? 32 : maxLength }}
          onChange={onChange}
          error={!!error?.type}
          helperText={isShowText && errorText}
        />
      </Grid>
    </Grid>
  );
};

export default AppTextField;
